import React from 'react';
import { Avatar, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';

export type LegalAnnoucementSuggestionProps = {
    id: number;
    label: string;
    selected: boolean;
    category: string;
    // eslint-disable-next-line react/no-unused-prop-types
    typePrestaId?: number;
    newspaper?: string;
    dateDemande?: string;
    advertiser?: string;
    dossiers?: Array<string>;
    onClick: (id: number) => void;
};

const LegalAnnoucementSuggestion: React.FunctionComponent<LegalAnnoucementSuggestionProps> =
    function (props) {
        const {
            id,
            label,
            category,
            selected,
            dateDemande,
            newspaper,
            advertiser,
            dossiers = [],
            onClick,
        } = props;
        const { palette, breakpoints } = useTheme();
        const downMd = useMediaQuery(breakpoints.down('md'));
        const selectable = dossiers.length <= 0;

        const handleClick = (choiceId: number) => {
            if (selectable) {
                onClick(choiceId);
            }
        };

        const wrapperSx = {
            display: 'flex',
            gap: '0 8px',
            alignItems: 'center',
            background: 'none',
            border: selected ? `solid 1px ${palette.grey[500]}` : `solid 1px ${palette.grey[300]}`,
            borderRadius: '12px',
            padding: '8px',
            transition: '0.1s ease',
            boxShadow: `0px 2px 2.5px 0px ${palette.grey[200]}`,
            opacity: selectable ? 1 : 0.5,
            cursor: selectable ? 'pointer' : 'auto',
            '&:hover': {
                border:
                    selectable || selected
                        ? `solid 1px ${palette.grey[500]}`
                        : `solid 1px ${palette.grey[300]}`,
            },
        };

        const avatarWrapperSx = {
            alignSelf: downMd ? 'flex-start' : 'auto',
            padding: '10px 8px',
        };

        const avatarSx = {
            display: 'flex',
            borderRadius: '8px',
            backgroundColor: selected
                ? palette.icon?.success?.backgroundColor
                : palette.icon?.primary?.backgroundColor,
            minHeight: '36px',
            minWidth: '36px',
        };

        const avatarIconSx = {
            fontSize: '16px',
            lineHeight: '16px',
            fontWeight: '400',
            color: selected ? palette.success.main : palette.primary.main,
        };

        const infoWrapperSx = {
            display: 'flex',
            flexDirection: 'column',
        };

        const inlinedWrapperSx = {
            display: 'flex',
            flexDirection: downMd ? 'column' : 'row',
            alignItems: downMd ? 'flex-start' : 'center',
            gap: downMd ? '4px 0' : '0 4px',
        };

        const nameSx = {
            color: palette.primary.dark,
            fontSize: '14px',
            lineHeight: '17px',
            fontWeight: '500',
        };

        const categorySx = {
            color: palette.grey[700],
            fontSize: '12px',
            lineHeight: '17px',
            fontWeight: '400',
        };

        const warningSx = {
            color: palette.error.dark,
            fontSize: '12px',
            lineHeight: '17px',
            fontWeight: '400',
        };

        const title = [label];

        if (advertiser) {
            title.push(` - ${advertiser}`);
        }

        const description = [category];
        const warning = [];
        const formattedDateDemande = dateDemande ? new Date(dateDemande) : null;

        if (formattedDateDemande) {
            description.push(` - Publié le ${formattedDateDemande.toLocaleDateString()}`);
        }

        if (newspaper) {
            description.push(` dans ${newspaper}`);
        }

        if (dossiers.length > 1) {
            warning.push(`Annonce deja raccrochée sur les dossiers ${dossiers.join(', ')}`);
        } else if (dossiers.length === 1) {
            warning.push(`Annonce deja raccrochée sur le dossier ${dossiers.join(', ')}`);
        }

        return (
            <Box
                sx={wrapperSx}
                data-testid="legal-annoucement-suggestion"
                onClick={() => handleClick(id)}
            >
                <Box sx={avatarWrapperSx}>
                    <Avatar
                        data-testid="document-icon"
                        sx={avatarSx}
                    >
                        <FaOptionIcon
                            icon={['fasl', 'newspaper']}
                            sx={avatarIconSx}
                        />
                    </Avatar>
                </Box>
                <Box sx={infoWrapperSx}>
                    <Box sx={inlinedWrapperSx}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px 0',
                            }}
                        >
                            <Typography
                                component="span"
                                sx={nameSx}
                            >
                                {title.join('')}
                            </Typography>
                            <Typography
                                component="span"
                                sx={categorySx}
                            >
                                {description.join('')}
                            </Typography>
                            {warning && (
                                <Typography
                                    component="span"
                                    sx={warningSx}
                                >
                                    {warning.join('')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

export default LegalAnnoucementSuggestion;
